/*
 * Copyright 1999-2024 Jagex Ltd.
 */
import { observable, action } from 'mobx';
import { SOCIAL_PROVIDERS, UNKNOWN_STEAM_NAME } from '@common/constants';
import { Auth } from '@stores/auth/auth';
import { auth } from '@stores/auth/decorators';
import { loader } from '@stores/loading/decorators';
import { Loading } from '@stores/loading/loading';
import { notify } from '@stores/notifications/decorators';
import { Notifications } from '@stores/notifications/notifications';
import { Request } from './requests';
import {
  DeleteSocialLinkRequestParams,
  ValidateUrlsRequestParams,
  SocialFlowResponse,
  SocialFlowState,
  SocialLinkingProcess
} from './types';

export class Social {
  @observable private _steamProfileName: string | null = null;

  constructor(private auth: Auth, private loading: Loading, private notifications: Notifications) {
    this.loading.registerLoader('linked-accounts-list', true);
  }

  @action
  setSteamProfileName(steamProfileName: string | null): void {
    this._steamProfileName = steamProfileName;
  }

  get steamProfileName(): string | null {
    return this._steamProfileName;
  }

  @auth
  async startSocialLinking(): Promise<SocialLinkingProcess> {
    return Request.startSocialLinking();
  }

  @auth
  async getSocialFlow(flowId: string): Promise<SocialFlowResponse> {
    const socialFlow = await Request.getSocialFlow(flowId);

    if (socialFlow.state === SocialFlowState.DataFetched && socialFlow.data.provider === SOCIAL_PROVIDERS.steam) {
      this.setSteamProfileName(socialFlow.data.providerAccountInfo.personaname ?? UNKNOWN_STEAM_NAME);
    }

    return socialFlow;
  }

  @auth
  async completeSocialLinking(linkingId: string, socialFlowId: string): Promise<SocialLinkingProcess> {
    return Request.completeSocialLinking(linkingId, { socialFlowId });
  }

  @auth
  async cancelSocialLinking(linkingId: string, socialFlowId: string): Promise<void> {
    return Request.cancelSocialLinking(linkingId, { socialFlowId });
  }

  @loader('social--unlink-account')
  @notify()
  @auth
  unlinkSocialAccount({ id }: DeleteSocialLinkRequestParams): Promise<void> {
    return Request.unlink(id as string);
  }

  @auth
  async validateUrls(urls: ValidateUrlsRequestParams): Promise<void> {
    return Request.validate(urls);
  }

  steamCleanup(): void {
    this.setSteamProfileName(null);
  }
}
